import React from 'react';
import type { Hospital, OperatingRoom } from '../services/api.ts';
import AdminOnly from './AdminOnly.tsx';
import { colorForHospital } from './hospitalColors.ts';
import { formatAddress, HospitalSearchResult } from './hospitalSearch.ts';
import { timezoneLabel } from './hospitalTimezones.ts';

// One row per hospital; its rooms open underneath. Everything is read-only here — changes go through
// the Edit dialogs — so the same table serves admins and read-only users.

interface HospitalsTableProps {
  results: HospitalSearchResult<Hospital, OperatingRoom>[];
  totalCount: number;
  loading: boolean;
  searchQuery: string;
  isExpanded: (hospitalId: string) => boolean;
  onToggleExpand: (hospitalId: string) => void;
  onEditHospital: (hospital: Hospital) => void;
  onDeleteHospital: (hospital: Hospital) => void;
  onAddRoom: (hospital: Hospital) => void;
  onEditRoom: (room: OperatingRoom) => void;
  onDeleteRoom: (room: OperatingRoom) => void;
  deletingHospitalId: string | null;
  busyRoomId: string | null;
}

const COLUMN_COUNT = 9;
const note: React.CSSProperties = { display: 'block', fontSize: '0.75em', color: 'var(--text-muted)', marginTop: '2px' };

// THE TWO UNSET SITE TYPES MEAN DIFFERENT THINGS, so they get different notices: an unset surgery-center
// flag is still guessed from the hospital name, but an unset children's flag makes the site an ordinary
// adult one, whatever it is called. A shared "guessed from name" line would leave a pediatric-sounding
// site unmarked in the belief that its name made it pediatric.
const SiteType: React.FC<{ hospital: Hospital }> = ({ hospital }) => {
  const pills: React.ReactNode[] = [];
  if (hospital.isSurgeryCenter) pills.push(<span key="sc" className="pill pill--info">Surgery center</span>);
  if (hospital.isChildrensHospital) pills.push(<span key="ch" className="pill pill--success">Children’s</span>);
  if (pills.length === 0 && hospital.isSurgeryCenter === false && hospital.isChildrensHospital === false) {
    pills.push(<span key="gen" className="pill">General</span>);
  }
  return (
    <>
      {pills}
      {hospital.isSurgeryCenter === undefined && (
        <span style={note} title="With surgery-center status never set, it is still guessed from the hospital name, so a rename can change staffing. Set it in Edit.">
          Surgery center: not set — guessed from name
        </span>
      )}
      {hospital.isChildrensHospital === undefined && (
        <span style={note} title="Pediatric status comes from this setting alone — the hospital name is never consulted. Unset, the site is staffed as a general adult site.">
          Children’s: not set — staffed as a general adult site
        </span>
      )}
    </>
  );
};

const RoomFlags: React.FC<{ room: OperatingRoom }> = ({ room }) => {
  const flags: Array<[string, string]> = [];
  if (room.isCardiacRoom) flags.push(['Cardiac', 'pill--danger']);
  if (room.isCSectionRoom) flags.push(['C-Section', 'pill--success']);
  if (room.hasIonizingRadiation) flags.push(['Radiation', 'pill--warning']);
  if (room.isHazard) flags.push(['Hazard', 'pill--warning']);
  if (room.isDoctorAssignableOnly) flags.push(['Doctor only', 'pill--info']);
  if (flags.length === 0) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  return <>{flags.map(([label, tone]) => <span key={label} className={`pill ${tone}`}>{label}</span>)}</>;
};

const RoomsPanel: React.FC<{
  hospital: Hospital;
  rooms: OperatingRoom[];
  filtered: boolean;
  onAddRoom: HospitalsTableProps['onAddRoom'];
  onEditRoom: HospitalsTableProps['onEditRoom'];
  onDeleteRoom: HospitalsTableProps['onDeleteRoom'];
  busyRoomId: string | null;
}> = ({ hospital, rooms, filtered, onAddRoom, onEditRoom, onDeleteRoom, busyRoomId }) => (
  <div style={{ padding: '12px 14px', background: 'var(--bg-surface-raised)', borderRadius: '8px', margin: '4px 0' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
      <h4 style={{ margin: 0 }}>
        {filtered ? 'Matching rooms' : 'Operating rooms'} at {hospital.name} ({rooms.length})
      </h4>
      <div style={{ flex: 1 }} />
      <AdminOnly>
        <button type="button" className="btn btn-small" onClick={() => onAddRoom(hospital)} aria-label={`Add room to ${hospital.name}`}>
          + Add room
        </button>
      </AdminOnly>
    </div>
    {rooms.length === 0 ? (
      <p style={{ color: 'var(--text-muted)', margin: 0 }}>No rooms yet.</p>
    ) : (
      <table aria-label={`Rooms at ${hospital.name}`}>
        <thead>
          <tr>
            <th>Room</th>
            <th>Required Credential</th>
            <th>Room Type</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rooms.map(room => (
            <tr key={room.operatingRoomId}>
              <td>{room.name}</td>
              <td>{room.credential || <span style={{ color: 'var(--text-muted)' }}>None</span>}</td>
              <td><RoomFlags room={room} /></td>
              <td>
                <AdminOnly>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button type="button" className="btn btn-small" onClick={() => onEditRoom(room)} disabled={busyRoomId === room.operatingRoomId} aria-label={`Edit ${room.name}`}>
                      Edit
                    </button>
                    <button type="button" className="btn btn-small btn-danger" onClick={() => onDeleteRoom(room)} disabled={busyRoomId === room.operatingRoomId} aria-label={`Delete ${room.name}`}>
                      {busyRoomId === room.operatingRoomId ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                </AdminOnly>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const HospitalsTable: React.FC<HospitalsTableProps> = ({
  results, totalCount, loading, searchQuery, isExpanded, onToggleExpand,
  onEditHospital, onDeleteHospital, onAddRoom, onEditRoom, onDeleteRoom, deletingHospitalId, busyRoomId,
}) => {
  if (loading) return <p>Loading hospitals...</p>;
  const query = searchQuery.trim();
  if (results.length === 0) {
    return <p>{query ? `No hospitals or rooms found matching "${query}".` : 'No hospitals yet.'}</p>;
  }

  return (
    <>
      {query && (
        <small style={{ display: 'block', marginBottom: '8px', color: 'var(--text-muted)' }}>
          Showing {results.length} of {totalCount} hospitals
        </small>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Board Color</th>
              <th>Site Type</th>
              <th>Time Zone</th>
              <th>Operating Rooms</th>
              <th>Address</th>
              <th>Latitude</th>
              <th>Longitude</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {results.map(({ hospital, rooms, matchedByRoom }) => {
              const expanded = isExpanded(hospital.hospitalId);
              const deleting = deletingHospitalId === hospital.hospitalId;
              return (
                <React.Fragment key={hospital.hospitalId}>
                  <tr>
                    <td>{hospital.name}</td>
                    <td>
                      <span
                        aria-label={`Board color ${colorForHospital(hospital)}${hospital.color ? '' : ' (auto-assigned)'}`}
                        title={hospital.color ? 'Custom board color' : 'Auto-assigned board color'}
                        style={{ display: 'inline-block', width: '28px', height: '18px', borderRadius: '4px', border: '1px solid var(--border-soft)', background: colorForHospital(hospital), verticalAlign: 'middle' }}
                      />
                    </td>
                    <td><SiteType hospital={hospital} /></td>
                    <td>{hospital.timezone ? timezoneLabel(hospital.timezone) : <span style={{ color: 'var(--text-muted)' }}>Not set</span>}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-small"
                        onClick={() => onToggleExpand(hospital.hospitalId)}
                        aria-expanded={expanded}
                        aria-label={`${expanded ? 'Hide' : 'Show'} rooms at ${hospital.name}`}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {matchedByRoom ? `${rooms.length} matching` : `${rooms.length} room${rooms.length === 1 ? '' : 's'}`} {expanded ? '▴' : '▾'}
                      </button>
                    </td>
                    <td>{formatAddress(hospital.address)}</td>
                    <td>{hospital.latitude !== undefined ? hospital.latitude.toFixed(6) : 'N/A'}</td>
                    <td>{hospital.longitude !== undefined ? hospital.longitude.toFixed(6) : 'N/A'}</td>
                    <td>
                      <AdminOnly>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <button type="button" className="btn btn-small" onClick={() => onEditHospital(hospital)} disabled={deletingHospitalId !== null} aria-label={`Edit ${hospital.name}`}>
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-small btn-danger"
                            onClick={() => onDeleteHospital(hospital)}
                            disabled={deletingHospitalId !== null}
                            aria-label={`Delete ${hospital.name}`}
                            title={`Delete ${hospital.name} and all of its rooms, cases, and allocations`}
                          >
                            {deleting ? 'Deleting…' : 'Delete'}
                          </button>
                        </div>
                      </AdminOnly>
                    </td>
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={COLUMN_COUNT} style={{ background: 'transparent', padding: '0 4px' }}>
                        <RoomsPanel
                          hospital={hospital}
                          rooms={rooms}
                          filtered={matchedByRoom}
                          onAddRoom={onAddRoom}
                          onEditRoom={onEditRoom}
                          onDeleteRoom={onDeleteRoom}
                          busyRoomId={busyRoomId}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default HospitalsTable;
